import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  FilePdf, EnvelopeSimple, Eye, Warning, CheckCircle, Crown, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const QUARTERS = [1, 2, 3, 4];

const _prevQuarter = (now = new Date()) => {
  const q = Math.floor(now.getUTCMonth() / 3) + 1;
  if (q === 1) return { year: now.getUTCFullYear() - 1, quarter: 4 };
  return { year: now.getUTCFullYear(), quarter: q - 1 };
};

const AdminImpactEmails = () => {
  const initial = _prevQuarter();
  const [year, setYear] = useState(initial.year);
  const [quarter, setQuarter] = useState(initial.quarter);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSendResult, setLastSendResult] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const r = await axios.get(`${API}/admin/impact-email/preview?year=${year}&quarter=${quarter}`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load preview");
    } finally { setPreviewing(false); }
  };

  const runSend = async () => {
    if (!preview) {
      toast.error("Run a preview first so you know who you'll hit");
      return;
    }
    const confirmed = window.confirm(
      `About to send the Q${quarter} ${year} Impact PDF email to ${preview.recipient_count} recipient${preview.recipient_count === 1 ? "" : "s"}. This cannot be undone. Continue?`
    );
    if (!confirmed) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/impact-email/send?year=${year}&quarter=${quarter}`);
      setLastSendResult(r.data);
      toast.success(`Sent ${r.data.sent} of ${r.data.recipient_count} emails`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const currentYear = new Date().getUTCFullYear();

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl mx-auto py-6" data-testid="admin-impact-emails-page">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
            <FilePdf weight="fill" className="w-7 h-7 text-emerald-400" />
            Quarterly Impact Emails
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Auto-fires on the 1st of Apr/Jul/Oct/Jan at 9 AM ET. Use this page to preview, manually trigger, or backfill.
          </p>
        </div>

        {/* Quarter selector */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-base">Select a reporting period</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Year</label>
                <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}
                        className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm"
                        data-testid="year-select">
                  {[currentYear, currentYear - 1, currentYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Quarter</label>
                <div className="grid grid-cols-4 gap-1 mt-1" data-testid="quarter-buttons">
                  {QUARTERS.map((q) => (
                    <button key={q} onClick={() => setQuarter(q)}
                            className={`py-2 text-sm font-semibold rounded-sm transition ${quarter === q ? "bg-emerald-500 text-black" : "bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white"}`}
                            data-testid={`quarter-${q}`}>
                      Q{q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={runPreview} disabled={previewing}
                      variant="outline"
                      className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm"
                      data-testid="preview-btn">
                <Eye className="w-4 h-4 mr-1.5" /> {previewing ? "Loading…" : "Preview recipients"}
              </Button>
              <Button onClick={runSend} disabled={sending || !preview}
                      className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                      data-testid="send-btn">
                <EnvelopeSimple weight="fill" className="w-4 h-4 mr-1.5" /> {sending ? "Sending…" : `Send to ${preview?.recipient_count ?? "—"}`}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        {preview && (
          <Card className="bg-zinc-950 border-zinc-800" data-testid="preview-results">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Crown weight="fill" className="w-5 h-5 text-emerald-400" />
                {preview.recipient_count} recipient{preview.recipient_count === 1 ? "" : "s"} — Q{quarter} {year}
              </CardTitle>
              <a href={`${API}/sponsorships/impact-pdf?year=${year}&quarter=${quarter}`}
                 target="_blank" rel="noopener noreferrer"
                 className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                 data-testid="preview-pdf-link">
                Preview sample PDF <ArrowSquareOut className="w-3.5 h-3.5" />
              </a>
            </CardHeader>
            <CardContent>
              {preview.recipient_count === 0 ? (
                <div className="py-6 text-center text-zinc-500">
                  <Warning weight="fill" className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                  No users have logged sponsorships in this quarter. Nothing to send.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-900 text-zinc-500 uppercase text-[10px] tracking-wider font-mono">
                      <tr>
                        <th className="text-left px-3 py-2">Email</th>
                        <th className="text-left px-3 py-2">Business</th>
                        <th className="text-right px-3 py-2">Total</th>
                        <th className="text-right px-3 py-2">Logs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.previews.map((p) => (
                        <tr key={p.user_id} className="border-t border-zinc-900">
                          <td className="px-3 py-2 text-zinc-200">{p.email}</td>
                          <td className="px-3 py-2 text-zinc-300">{p.business_name}</td>
                          <td className="px-3 py-2 text-right text-emerald-400 font-bold">${p.total_dollars.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right text-zinc-500 font-mono">{p.log_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Send result */}
        {lastSendResult && (
          <Card className="bg-emerald-500/5 border-emerald-500/30" data-testid="send-result">
            <CardContent className="py-4 flex items-start gap-3">
              <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-zinc-100">Last send: {lastSendResult.sent}/{lastSendResult.recipient_count} delivered</p>
                {lastSendResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-amber-300">
                    <strong>{lastSendResult.errors.length} error(s):</strong>
                    <ul className="mt-1 space-y-0.5 font-mono">
                      {lastSendResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Schedule info */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader><CardTitle className="text-base">Automation schedule</CardTitle></CardHeader>
          <CardContent className="text-sm text-zinc-400 space-y-1.5">
            <p>• <strong className="text-zinc-200">Apr 1</strong> — sends Q1 (Jan–Mar) reports</p>
            <p>• <strong className="text-zinc-200">Jul 1</strong> — sends Q2 (Apr–Jun) reports</p>
            <p>• <strong className="text-zinc-200">Oct 1</strong> — sends Q3 (Jul–Sep) reports</p>
            <p>• <strong className="text-zinc-200">Jan 1</strong> — sends Q4 (Oct–Dec) reports</p>
            <p className="text-zinc-600 text-xs pt-2">All sends fire at 9–11 AM ET. Idempotent — won't double-send on restart. Each automated run is logged in <code className="text-emerald-400">db.system_jobs</code>.</p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminImpactEmails;
